import { useCallback, useEffect, useState } from "react";

export function useImageLightbox(imageUrls = []) {
  const urls = imageUrls.filter(Boolean);
  const [index, setIndex] = useState(null);

  const openAt = useCallback((nextIndex) => {
    if (!urls.length) return;
    const safe = Math.max(0, Math.min(nextIndex, urls.length - 1));
    setIndex(safe);
  }, [urls.length]);

  const close = useCallback(() => setIndex(null), []);

  const goPrev = useCallback(() => {
    setIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
  }, []);

  const goNext = useCallback(() => {
    setIndex((prev) =>
      prev !== null && prev < urls.length - 1 ? prev + 1 : prev
    );
  }, [urls.length]);

  useEffect(() => {
    if (index === null) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowLeft") goPrev();
      if (event.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [index, close, goPrev, goNext]);

  useEffect(() => {
    if (index === null) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [index]);

  const lightbox =
    index !== null && urls[index] ? (
      <ImageLightboxOverlay
        src={urls[index]}
        index={index}
        total={urls.length}
        onClose={close}
        onPrev={goPrev}
        onNext={goNext}
      />
    ) : null;

  return { openAt, close, lightbox, isOpen: index !== null };
}

function ImageLightboxOverlay({ src, index, total, onClose, onPrev, onNext }) {
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="图片预览"
    >
      <div
        className="relative max-w-5xl w-full flex items-center justify-center"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 bg-white/90 text-gray-800 rounded-full w-10 h-10 text-2xl leading-none hover:bg-white"
          aria-label="关闭"
        >
          ×
        </button>

        {total > 1 && index > 0 ? (
          <button
            type="button"
            onClick={onPrev}
            className="absolute left-2 z-10 bg-white/90 text-gray-800 rounded-full w-10 h-10 text-2xl leading-none hover:bg-white"
            aria-label="上一张"
          >
            ‹
          </button>
        ) : null}

        <img
          src={src}
          alt="放大预览"
          className="max-h-[85vh] max-w-full rounded-xl shadow-2xl object-contain select-none"
        />

        {total > 1 && index < total - 1 ? (
          <button
            type="button"
            onClick={onNext}
            className="absolute right-2 z-10 bg-white/90 text-gray-800 rounded-full w-10 h-10 text-2xl leading-none hover:bg-white"
            aria-label="下一张"
          >
            ›
          </button>
        ) : null}

        {total > 1 ? (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/90 text-sm bg-black/40 px-3 py-1 rounded-full">
            {index + 1} / {total}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ClickableImage({
  src,
  alt = "",
  className = "",
  onOpen,
  disabled = false,
}) {
  if (!src || disabled) {
    return (
      <img src={src} alt={alt} className={className} />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} cursor-zoom-in transition hover:opacity-95`}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen?.();
        }
      }}
      role="button"
      tabIndex={0}
      title="点击查看大图"
    />
  );
}

/** 从用户资料 photos 字段解析 URL 列表 */
export function resolveUserPhotoUrls(photos = []) {
  return photos
    .map((photo) =>
      typeof photo === "string"
        ? photo
        : photo?.image_url || photo?.url || ""
    )
    .filter(Boolean);
}

/** 头像 + 生活照合并为可浏览列表（头像在前） */
export function buildProfileGalleryUrls(avatar, photos = []) {
  const photoUrls = resolveUserPhotoUrls(photos);
  const avatarUrl = avatar || "";
  if (avatarUrl && photoUrls.includes(avatarUrl)) {
    return photoUrls;
  }
  if (avatarUrl) {
    return [avatarUrl, ...photoUrls];
  }
  return photoUrls;
}
